<template>
  <validation-observer tag="div" ref="form">
    <div class="row">
      <div class="col-md-6">
        <validation-provider
          vid="balancing_transaction_id"
          :name="lodash.upperFirst($t('balancings.ticket_id'))"
          rules="required"
          v-slot="{ validated, errors }"
        >
          <b-form-group
            class="row mb-3"
            label-cols-md="12"
            :label="`${lodash.upperFirst($t('balancings.id'))}*`"
            :state="errors[0] ? false : validated ? true : null"
            :invalid-feedback="errors[0]"
            label-class="body-font-sm"
          >
            <multiselect-ajax
              :key="`${_uid}-balancing-transaction-id`"
              name="ticket_id"
              v-model.number="model.balancing_transaction_id"
              :custom-label="({ id }) => `${id}`"
              url="/balancing_transactions"
              :params="{
                remaining_debt__gt: 0,
                with: [
                  'sale_order',
                  'sale_order.ticket',
                  'sale_order.sale_items.purchase_item.purchase_order.paymentables.payment',
                  'balancing_payments',
                  'sale_order.trip'
                ].join(','),
                with_count: ['balancing_payments'].join(',')
              }"
              :disabled="!!model.id"
              @update:selected="onBalancingUpdated"
            >
            </multiselect-ajax>
          </b-form-group>
        </validation-provider>
      </div>
      <div class="col-md-6" v-if="model.balancing && model.balancing.id && model.balancing_transaction_id">
        <b-form-group
          class="row mb-3"
          label-cols-md="12"
          :label="`${lodash.upperFirst($t('balancing_payments.balancing_status'))}`"
          label-class="body-font-sm"
        >
          <div :class="`badge badge-pill font-size-11 badge-soft-${statusBadgeVariant}`">
            <span v-html="status"></span>
          </div>
        </b-form-group>
      </div>
    </div>
    <div v-if="model.balancing && model.balancing.id && model.balancing_transaction_id">
      <div class="row">
        <div class="col-md-6 mb-16">
          <div>
            <div class="heading-font-5 mb-4px">Nominal Balancing</div>
            <div>{{ $numeral(model.balancing.debt + model.balancing.adjustment).format("$ 0,0.[00]") }}</div>
          </div>
        </div>
        <div class="col-md-6 mb-16">
          <div>
            <div class="heading-font-5 mb-4px">Sisa</div>
            <div>
              {{
                $numeral(model.balancing.debt + model.balancing.adjustment - paymentTotal - model.credit).format(
                  "$ 0,0.[00]"
                )
              }}
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6 mb-16">
          <div>
            <div class="heading-font-5">No. Folder</div>
            <div>
              <router-link :to="`/tickets/${model.balancing.sale_order.ticket_id}`">
                {{ model.balancing.sale_order.ticket_id }}
              </router-link>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-16">
          <div>
            <div class="heading-font-5">No. Bongkar</div>
            <div>
              <router-link :to="`/unloads/${model.balancing.sale_order.id}`"
                >{{ model.balancing.sale_order.id }}
              </router-link>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-16">
          <div>
            <div class="heading-font-5">No. Penjualan</div>
            <div>
              <router-link :to="`/sale_notes/${model.balancing.sale_order.id}`">
                {{ model.balancing.sale_order.id }}
              </router-link>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-16">
          <div>
            <div class="heading-font-5">
              Purchase Ref
              <i
                class="bx bx-info-circle"
                v-tooltip="{
                  content: `Referensi PI, PO, DP, dari<br/> platform eksternal`,
                  html: true,
                  theme: 'info-tooltip',
                  placement: 'bottom'
                }"
              ></i>
            </div>
            <div>
              {{ purchaseInvoices }}
            </div>
          </div>
        </div>
      </div>
      <hr />
      <div class="section-title">Form Pembayaran</div>
      <div class="row">
        <div class="col-md-6">
          <div class="row">
            <validation-provider
              class="col-md-12"
              v-slot="{ validated, errors }"
              :name="lodash.upperFirst($t('purchase_payments.from'))"
              rules="required"
              vid="from_customer_id"
            >
              <b-form-group
                :invalid-feedback="errors[0]"
                :label="lodash.startCase($t('purchase_payments.from')) + '*'"
                :state="errors[0] ? false : validated ? true : null"
                class="row mb-3"
                label-cols-md="12"
                label-class="body-font-sm"
              >
                <multiselect-ajax
                  :key="`customers-${model.ticket_id}`"
                  v-model.number="model.from_customer.id"
                  :custom-label="m => m.name"
                  :disabled="!!model.id"
                  :params="customerParams({ type__like_in: 'jala' })"
                  :url="`/customers`"
                  name="from_customer_id"
                >
                </multiselect-ajax>
              </b-form-group>
            </validation-provider>
            <validation-provider
              class="col-md-12"
              v-slot="{ validated, errors }"
              :name="lodash.upperFirst($t('purchase_payments.from_bank_account'))"
              rules="required"
              vid="from_bank_account"
            >
              <b-form-group
                :invalid-feedback="errors[0]"
                :label="lodash.startCase($t('purchase_payments.from_bank_account')) + '*'"
                :state="errors[0] ? false : validated ? true : null"
                class="row mb-3"
                label-cols-md="12"
                label-class="body-font-sm"
              >
                <multiselect-ajax
                  :key="`customers-${model.from_customer.id}`"
                  v-model.number="model.from_bank_account_id"
                  :custom-label="m => `${m.account_number} - ${m.bank.name}`"
                  :disabled="!model.from_customer.id || !!model.id"
                  :params="bankAccountParams(model.from_customer.id ? { customer_id: model.from_customer.id } : {})"
                  :url="`/bank_accounts`"
                  name="from_bank_account"
                >
                </multiselect-ajax>
              </b-form-group>
            </validation-provider>
          </div>
        </div>
        <div class="col-md-6">
          <div class="row">
            <validation-provider
              class="col-md-12"
              v-slot="{ validated, errors }"
              :name="lodash.upperFirst($t('purchase_payments.to'))"
              rules="required"
              vid="to_customer_id"
            >
              <b-form-group
                :invalid-feedback="errors[0]"
                :label="lodash.startCase($t('purchase_payments.to')) + '*'"
                :state="errors[0] ? false : validated ? true : null"
                class="row mb-3"
                label-cols-md="12"
                label-class="body-font-sm"
              >
                <multiselect-ajax
                  :key="`${_uid}-customers-${model.to_customer.id}-${selectedCustomerType}`"
                  v-model.number="model.to_customer.id"
                  :custom-label="m => `${m.name}`"
                  :disabled="!((model.balancing || {}).sale_order || {}).ticket_id || !!model.id"
                  :params="customerParams({ type__like_in: `lcs,brs,farmer` })"
                  :url="`/customers`"
                  name="to_customer_id"
                >
                  <template v-slot:beforeList>
                    <b-button v-b-modal.customer-create-modal variant="link">
                      + {{ $t("Add new {something}", { something: $t("receipt_orders.customer") }) }}
                    </b-button>
                    <customer-create-modal
                      id="customer-create-modal"
                      :key="`customer-create-modal-${selectedCustomerType}-${model.to_customer_id}`"
                      :deny-customer-select="true"
                      :initial-value="
                        selectedCustomerType
                          ? { type: [selectedCustomerType], registered: false }
                          : { registered: false }
                      "
                      @created="$set(model.to_customer, 'id', $event.id)"
                    ></customer-create-modal>
                  </template>
                </multiselect-ajax>
              </b-form-group>
            </validation-provider>
            <validation-provider
              class="col-md-12"
              v-slot="{ validated, errors }"
              :name="lodash.upperFirst($t('purchase_payments.to_bank_account'))"
              rules="required"
              vid="to_bank_account"
            >
              <b-form-group
                :invalid-feedback="errors[0]"
                :label="lodash.startCase($t('purchase_payments.to_bank_account')) + '*'"
                :state="errors[0] ? false : validated ? true : null"
                class="row mb-3"
                label-cols-md="12"
                label-class="body-font-sm"
              >
                <multiselect-ajax
                  :key="`$customers-${model.to_customer.id}`"
                  v-model.number="model.to_bank_account_id"
                  :custom-label="m => `${m.account_number} (${m.bank.name})`"
                  :disabled="!model.to_customer.id || !!model.id"
                  :params="bankAccountParams(model.to_customer.id ? { customer_id: model.to_customer.id } : {})"
                  :url="`/bank_accounts`"
                  name="to_bank_account"
                >
                  <template v-if="model.to_customer.id" v-slot:beforeList>
                    <b-button v-b-modal.bank-account-create variant="link">
                      + {{ $t("Add new {something}", { something: $t("purchase_payments.to_bank_account") }) }}
                    </b-button>
                    <bank-account-create
                      id="bank-account-create"
                      :key="`bank-account-create-${model.to_bank_account_id}`"
                      :initial-value="{ customer_id: model.to_customer.id }"
                      @created="$set(model, 'to_bank_account_id', $event.id)"
                    ></bank-account-create>
                  </template>
                </multiselect-ajax>
              </b-form-group>
            </validation-provider>
          </div>
        </div>
        <div class="col-md-6 mb-16">
          <div>
            <validation-provider
              v-slot="{ validated, errors }"
              :name="lodash.startCase($t('balancing_payments.adjustment'))"
              vid="adjustment"
            >
              <b-form-group
                :invalid-feedback="errors[0]"
                :label="`${lodash.startCase($t('balancing_payments.adjustment'))}*`"
                :state="errors[0] ? false : validated ? true : null"
                class="row"
                label-cols-md="12"
                label-class="pt-0 pb-4px body-font-sm"
              >
                <b-input-group prepend="Rp">
                  <numeral-input
                    id="adjustment"
                    :key="`adjustment-${model.purchase_order_id}`"
                    v-model="model.balancing.adjustment"
                    :placeholder="`${lodash.upperFirst($t('balancing_payments.adjustment'))}*`"
                    :state="errors[0] ? false : validated ? true : null"
                    format="0,0.[00]"
                    name="adjustment"
                    @input="onAdjustmentChanged"
                    :disabled="model.balancing && model.balancing.balancing_payments_count > 0"
                  />
                </b-input-group>
              </b-form-group>
            </validation-provider>
          </div>
        </div>
        <div class="col-md-6 mb-16">
          <validation-provider
            v-slot="{ validated, errors }"
            :name="lodash.startCase($t('balancing_payments.credit'))"
            :rules="creditRules"
            vid="credit"
          >
            <b-form-group
              :invalid-feedback="errors[0]"
              :label="`${lodash.upperFirst($t('balancing_payments.credit'))}*`"
              :state="errors[0] ? false : validated ? true : null"
              class="row"
              label-cols-md="12"
              label-class="pt-0 pb-4px body-font-sm"
            >
              <b-input-group prepend="Rp">
                <numeral-input
                  id="credit"
                  :key="`credit-${model.purchase_order_id}`"
                  v-model="model.credit"
                  :placeholder="`${lodash.upperFirst($t('balancing_payments.credit'))}*`"
                  :state="errors[0] ? false : validated ? true : null"
                  format="0,0.[00]"
                  name="credit"
                />
              </b-input-group>
            </b-form-group>
          </validation-provider>
        </div>
        <div class="col-md-6 mb-16">
          <validation-provider
            v-slot="{ validated, errors }"
            :name="lodash.startCase($t('balancing_payments.paid_at'))"
            :rules="`required|before_or_equal:${$moment().format('YYYY-MM-DD')}`"
            vid="ordered_at"
          >
            <b-form-group
              :invalid-feedback="errors[0]"
              :label="lodash.startCase($t('balancing_payments.paid_at')) + '*'"
              :state="errors[0] ? false : validated ? true : null"
              class="row"
              label-cols-md="12"
              label-class="pt-0 pb-4px body-font-sm"
            >
              <date-picker
                :disabled="!!model.id"
                :state="errors[0] ? false : validated ? true : null"
                :value="model.paid_at"
                format="DD MMMM YYYY"
                name="ordered_at"
                required
                type="date"
                @input="model.paid_at = $event"
              >
              </date-picker>
            </b-form-group>
          </validation-provider>
        </div>
        <div v-if="isDepositGood" class="col-md-6 mb-16">
          <validation-provider
            v-slot="{ validated, errors }"
            :name="lodash.startCase($t('balancing_payments.purchase_reference'))"
            vid="invoice_number"
          >
            <b-form-group
              :invalid-feedback="errors[0]"
              :label="`${lodash.upperFirst($t('balancing_payments.purchase_reference'))}`"
              :state="errors[0] ? false : validated ? true : null"
              class="row"
              label-cols-md="12"
              label-class="pt-0 pb-4px body-font-sm"
            >
              <b-form-input
                id="invoice_number"
                :name="`invoice_number`"
                type="text"
                :placeholder="lodash.upperFirst($t('balancing_payments.purchase_reference'))"
                :state="errors[0] ? false : validated ? true : null"
                v-model="model.invoice_number"
              ></b-form-input>
            </b-form-group>
          </validation-provider>
        </div>
      </div>
      <div class="row"></div>
      <hr />
      <div class="row">
        <div class="col-md-6 mb-3">
          <validation-provider
            v-slot="{ validated, errors }"
            :name="lodash.upperFirst($t('balancing_payments.transfer_slip'))"
            :rules="'required|ext:jpeg,jpg,png,pdf,jfif'"
            vid="transfer_slip"
          >
            <b-form-group
              :invalid-feedback="errors[0]"
              :label="`${lodash.upperFirst($t('balancing_payments.transfer_slip'))}*`"
              :state="errors[0] ? false : validated ? true : null"
            >
              <b-form-file
                v-model="model.transfer_slip.file"
                :disabled="!!model.id"
                :drop-placeholder="lodash.upperFirst($t('drop file here'))"
                :placeholder="lodash.upperFirst($t('choose a file or drop it here'))"
                :state="errors[0] ? false : validated ? true : null"
                accept="image/*"
                name="`transfer_slip`"
                @change="onFileChange('transfer_slip', $event)"
              ></b-form-file>
              <attachment-preview
                :key="`transfer-slip-${model.transfer_slip.preview}`"
                :format="model.transfer_slip.extension"
                :preview="model.transfer_slip.preview"
                :file="model.transfer_slip"
                class="mt-3"
              />
            </b-form-group>
          </validation-provider>
        </div>
        <template v-for="(optional_file, key) in model.optional_files.filter(r => r._action !== 'delete')">
          <div :key="`optional_files.key.${key}`" class="col-md-6 mb-3">
            <validation-provider
              :vid="`optional_files.${key}.file`"
              :name="lodash.upperFirst($t('balancing_payments.optional_file'))"
              v-slot="{ validated, errors }"
            >
              <b-form-group
                :label="lodash.upperFirst($t('balancing_payments.optional_file')) + ` - ${key + 1}`"
                :state="errors[0] ? false : validated ? true : null"
                :invalid-feedback="errors[0]"
              >
                <b-form-file
                  v-model="optional_file['file']"
                  :name="`optional_files[${key}][file]`"
                  :state="errors[0] ? false : validated ? true : null"
                  :placeholder="lodash.upperFirst($t('choose a file or drop it here'))"
                  :drop-placeholder="lodash.upperFirst($t('drop file here'))"
                  accept="image/*"
                  @change="onOptionalFileChange(key, $event)"
                ></b-form-file>
                <attachment-preview
                  :key="`transfer-slip-${model.transfer_slip.preview}`"
                  :preview="optional_file.preview"
                  :format="optional_file.extension"
                  :file="optional_file"
                  class="mt-3"
                />
              </b-form-group>
            </validation-provider>
            <div :key="`files.key.${key}.delete`" class="d-flex justify-content-end">
              <b-button
                size="sm"
                tag="span"
                variant="link"
                class="text-danger"
                @click="removeItem('optional_files', key)"
                >{{ $t("Delete") }}
              </b-button>
            </div>
          </div>
        </template>
      </div>
      <div class="d-flex justify-content-end">
        <b-button @click="addItem('optional_files')" variant="link" class="text-primary">Tambah Dokumen </b-button>
      </div>
      <div class="mt-4">
        <validation-provider
          v-slot="{ validated, errors }"
          :name="lodash.upperFirst($t('models.description'))"
          rules="max:255"
          vid="description"
        >
          <b-form-group
            :invalid-feedback="errors[0]"
            :label="lodash.upperFirst($t('purchase_payments.description'))"
            :state="errors[0] ? false : validated ? true : null"
            class="row mb-3"
          >
            <editor
              v-if="$parent.$parent.$options.name !== 'BModal'"
              ref="editor"
              v-model="model.description"
              :init="editorInit"
              class="form-control"
              inline
              plugins="lists link image paste help wordcount"
              toolbar="undo redo | styleselect | fontselect | bold italic | alignleft aligncenter alignright alignjustify | outdent indent"
            />
            <b-form-textarea v-else v-model="model.description" />
          </b-form-group>
        </validation-provider>
      </div>
    </div>
  </validation-observer>
</template>

<script>
import FormModel from "./model";
import BankAccountCreate from "@/components/modals/bank-account-create.vue";
import CustomerCreateModal from "@/components/modals/customer-create.vue";
import AttachmentPreview from "@/components/inputs/attachment-preview.vue";
// import CustomerCreateModal from '../modals/customer-create';
// import PreviewImagePdf from "@/components/inputs/preview-image-pdf";
// import BankAccountCreate from "@/components/modals/bank-account-create";

export default {
  extends: FormModel,
  components: {
    CustomerCreateModal,
    BankAccountCreate,
    AttachmentPreview
    // BankAccountCreate,
    // CustomerCreateModal,
    // PreviewImagePdf,
    // PurchaseOrderCreateModal,
    // TicketCreateModal
  },
  data() {
    return {
      selectedCustomerType: null,
      credit_max_value_rule: null,
      file_accepts: ["image/jpeg", "image/jpg", "image/png", "application/pdf"],
      backupFile: {
        invoice_document: !this.lodash.isEmpty(this.value.invoice_document)
          ? this.value.invoice_document
          : { name: "invoice_document" },
        optional_files: !this.lodash.isEmpty(this.value.optional_files) ? this.value.optional_files : [],
        transfer_slip: !this.lodash.isEmpty(this.value.transfer_slip)
          ? this.value.transfer_slip
          : { name: "transfer_slip" }
      },
      model: {
        description: "",
        balancing_transaction_id: null,
        ...this.value,
        invoice_number: !this.lodash.isEmpty(this.value.invoice_number) ? this.value.invoice_number : "",
        credit: !this.lodash.isEmpty(this.value.credit) ? this.value.credit : 0,
        files: [
          ...((this.value.files || []).length
            ? this.value.files
            : [{ name: "transfer slip" }, { name: "invoice document" }])
        ],
        balancing: {
          id: null,
          adjustment: 0,
          debt: 0,
          remaining_debt: 0,
          remaining_receivable: 0,
          sale_order: null,
          total_debt: 0
        },
        from_customer: !this.lodash.isEmpty(this.value.from_customer) ? this.value.from_customer : {},
        from_bank_account_id: !this.lodash.isEmpty(this.value.from_bank_account_id)
          ? this.value.from_bank_account_id
          : null,
        invoice_document: !this.lodash.isEmpty(this.value.invoice_document)
          ? this.value.invoice_document
          : { name: "invoice_document" },
        optional_files: !this.lodash.isEmpty(this.value.optional_files) ? this.value.optional_files : [{}],
        paymentable: !this.lodash.isEmpty(this.value.paymentable)
          ? this.value.paymentable
          : {
              id: null,
              ticketable: { ticket: {} }
            },
        paymentables: this.value.paymentables ?? [],
        ticket: !this.lodash.isEmpty(this.value.ticket) ? this.value.ticket : {},
        to_customer: !this.lodash.isEmpty(this.value.to_customer) ? this.value.to_customer : {},
        to_bank_account_id: !this.lodash.isEmpty(this.value.to_bank_account_id) ? this.value.to_bank_account_id : null,
        transfer_slip: !this.lodash.isEmpty(this.value.transfer_slip)
          ? this.value.transfer_slip
          : { name: "transfer_slip" }
      }
    };
  },
  computed: {
    paymentTotal() {
      return this.model.balancing?.balancing_payments?.reduce((acc, curr) => acc + curr.credit, 0) ?? 0;
    },
    isDepositGood() {
      return this.model.balancing?.sale_order?.type === "deposit_goods";
    },
    purchaseInvoices() {
      const ids = this.model.balancing.sale_order.sale_items
        .map(item =>
          item.purchase_item.purchase_order.paymentables.map(paymentable => paymentable.payment.invoice_number)
        )
        .flat()
        .filter(id => !!id);
      const uniqueIds = [...new Set(ids)];
      if (uniqueIds.length === 0) {
        return "-";
      }
      return uniqueIds.join(", ");
    },
    status() {
      const remaining_debt = this.model.balancing.debt + this.model.balancing.adjustment - this.paymentTotal;
      if (this.model.balancing?.balancing_payments_count === 0) return this.lodash.upperFirst(this.$t("unpaid"));
      if (remaining_debt === 0) return this.lodash.upperFirst(this.$t("paid off"));
      return remaining_debt > 0
        ? this.lodash.upperFirst(
            this.$t("underpaid {nominal}", { nominal: `${this.$numeral(remaining_debt).format("$ 0,0.[00]")}` })
          )
        : this.lodash.upperFirst(this.$t("overpaid"));
    },
    statusBadgeVariant() {
      const remaining_debt = this.model.balancing.debt + this.model.balancing.adjustment - this.paymentTotal;
      if (this.model.balancing.balancing_payments_count === 0) return "danger";
      if (remaining_debt === 0) return "success";
      return remaining_debt > 0 ? "warning" : "success";
    },
    ticketParams() {
      if (!this.model.id) {
        return {
          available_to_add_purchase_payment: 1,
          sort: "id,desc",
          with: "creator,purchase_orders"
        };
      }

      return {
        id: this.model.ticket_id,
        with: "creator"
      };
    },
    purchaseOrderParams() {
      if (!this.model.id) {
        return {
          unpaid: 1,
          with: "customer",
          where_has: `tickets,id,${this.model.ticket_id}`
        };
      }

      return {
        id__in: this.model.paymentables.map(({ paymentable_id }) => paymentable_id).join(","),
        with: "customer"
      };
    },
    hasDownPaymentPaymentable() {
      if (!this.model.paymentables.length) return false;

      return !!this.model.paymentables.find(({ type}) => type === 'deposit_goods');
    },

    creditRules() {
      let rule = "required|gt:0";
      return this.credit_max_value_rule ? `${rule}|max_value:${this.credit_max_value_rule}` : rule;
    },
    isDownPayment() {
      return this.model.paymentable?.product?.code === "down_payment";
    },
    remainingDebt() {
      return this.model?.paymentables.reduce((carry, { remaining_debt }) => carry + remaining_debt, 0) ?? 0;
    }
  },
  watch: {
    "model.ticket_id": function(val, oldVal) {
      if (!this.lodash.isEqual(val, oldVal)) {
        this.$set(this.model, "to_customer_id", null);
      }
    },
    "model.to_customer_id": function(n, o) {
      if (!this.lodash.isEqual(n, o)) this.model.to_bank_account_id = null;
    },
    "model.from_customer_id": function(n, o) {
      if (!this.lodash.isEqual(n, o)) this.model.from_bank_account_id = null;
    },
    "model.paymentables": function() {
      // this.credit_max_value_rule = this.remainingDebt;
      // if (!this.model.id) this.model.credit = this.remainingDebt;
    }
    // 'model.balancing': function (balancing) {
    //     this.$set(this.model, 'credit', balancing.remaining_debt);
    // },
    // 'model.balancing.adjustment': function (adjustment){
    //     if(typeof adjustment !== 'number'){
    //         adjustment = 0;
    //     }
    //     this.$set(this.model.balancing, 'remaining_debt', this.model.balancing.remaining_debt + adjustment)
    //     this.$set(this.model.balancing, 'total_debt', this.model.balancing.total_debt + adjustment)
    // },
  },
  mounted() {
    // this.checkLocalStorage()
    const params = this.$route.params;
    this.$set(this, "model", {
      ...this.model,
      ...params
    });
  },
  methods: {
    onAdjustmentChanged(adjustment) {
      if (typeof adjustment !== "number") {
        adjustment = 0;
        // this.model.balancing.adjustment = 0;
      }
      // this.$set(this.model, 'remaining_debt', this.model.balancing.remaining_debt + adjustment)
      // this.$set(this.model, 'total_debt', this.model.balancing.total_debt + adjustment)

      this.$set(this.model, "total_debt", this.model.balancing.total_debt);
      const remainingDebt = this.model.balancing.total_debt - this.paymentTotal + this.model.balancing.adjustment;
      this.$set(this.model, "remaining_debt", remainingDebt);
      this.$set(this.model, "credit", remainingDebt);
    },
    checkLocalStorage() {
      let local = localStorage.getItem("purchase_payment");
      if (local && !this.model.id) {
        let jsonData = JSON.parse(local);
        this.$set(this, "model", {
          ...jsonData
        });
        // clear
        localStorage.removeItem("purchase_payment");
      }
    },
    addItem(key) {
      this.model[key].push({});
    },
    removeItem(key, index) {
      const purchaseItem = this.model[key][index];
      if ((purchaseItem || {}).id) {
        this.$set(this.model[key][index], "_action", "delete");
      } else {
        this.model[key].splice(index, 1);
      }
    },
    onFileChange(key, file) {
      let vm = this;
      let reader = new FileReader();
      let data = file.target.files[0];
      if (!data) {
        setTimeout(function() {
          vm.model[key] = { ...vm.backupFile[key] };
          return null;
        }, 1000);
      }
      if (this.file_accepts.includes(data.type)) {
        reader.readAsDataURL(data);
        reader.onload = () => {
          this.$set(this.model[key], "preview", reader.result);
          this.$set(this.model[key], "extension", data.type);
          this.$set(this.backupFile, key, { ...this.model[key] });
        };
      }
    },
    onOptionalFileChange(key, file) {
      let reader = new FileReader();
      let data = file.target.files[0];

      if (this.file_accepts.includes(data.type)) {
        reader.readAsDataURL(data);
        reader.onload = () => {
          this.$set(this.model.optional_files[key], "preview", reader.result);
          this.$set(this.model.optional_files[key], "extension", data.type);
        };
      }
    },
    onPurchaseOrderSelected(payload) {
      // this.$set(this.model, 'paymentable', {...this.model.paymentable, ...payload})

      if (!Array.isArray(payload)) payload = [payload];
      this.model.paymentables = payload.map(({ id, remaining_debt }) => ({
        paymentable_id: id,
        paymentable_type: "App\\Models\\PurchaseOrder",
        credit: remaining_debt,
        remaining_debt
      }));
    },
    fileLabel(index) {
      let label = this.lodash.startCase(this.$t("purchase_payments.optional_file"));
      if (index === 0) {
        label = this.lodash.startCase(this.$t("purchase_payments.transfer_slip")) + "*";
      } else if (index === 1) {
        label = this.lodash.startCase(this.$t("purchase_payments.purchase_document"));
      }
      return label;
    },
    bankAccountParams(params) {
      let defaultParams = {
        with: "bank",
        active: 1
      };

      if (this.model.id) {
        delete defaultParams.active;
      }

      return { ...defaultParams, ...params };
    },
    customerParams(params) {
      let defaultParams = {
        active: 1
      };

      if (this.model.id) {
        delete defaultParams.active;
      }

      return { ...defaultParams, ...params };
    },
    onBalancingUpdated(payload) {
      if (payload) {
        this.$set(this.model, "balancing", payload);
        this.$set(this.model, "ticket_id", payload.sale_order.ticket_id);
        this.$set(this.model, "total_debt", payload.total_debt);
        this.$set(this.model, "remaining_debt", payload.total_debt - this.paymentTotal + payload.adjustment);
        this.$set(this.model, "credit", payload.remaining_debt);
        this.$set(this.model.balancing, "adjustment", payload.adjustment);
        // this.model.credit = payload.remaining_debt;
        this.selectedCustomerType = payload?.ticket?.customer_type;
        this.paymentable = {
          paymentable_id: payload.id,
          paymentable_type: "App\\Models\\BalancingTransaction",
          ...payload
        };

        this.$nextTick(() => {
          this.$set(this.model.balancing, "adjustment", payload.adjustment);
        });
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "~@/design/colors";

::v-deep {
  .col-form-label {
    color: $neutral-default;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 14px; /* 116.667% */
  }

  input,
  .input-group-prepend {
    height: 32px;
  }
}

.delete-item {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
}

.heading-font-5 {
  color: var(--neutral-800, var(--colors-slate-800, #1e293b));
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px; /* 153.846% */
  letter-spacing: 0.26px;
}

.section-title {
  color: var(--neutral-700, var(--colors-slate-700, #334155));
  font-size: 16px;
  font-style: normal;
  font-weight: 800;
  line-height: 20px; /* 125% */
  letter-spacing: 0.8px;
}
</style>
